import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Download, FileText, Trash2, Package, Pill } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function BatchesPage() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/batches");
      setBatches(data);
    } catch {
      toast.error("Error al cargar los productos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const downloadSap = async (batchId, descripcion) => {
    try {
      const res = await api.get(`/batches/${batchId}/sap`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SAP_${(descripcion || "EXIS").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Descarga SAP iniciada");
    } catch (e) {
      toast.error("Error al descargar SAP");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/batches/${id}`);
      toast.success(isAdmin ? "Envío eliminado" : "Producto eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const renderProduct = (b) => {
    const list = b.products_summary || [];
    if (list.length === 0) {
      return <span className="text-slate-400 italic">Sin productos</span>;
    }
    const first = list[0];
    const rest = list.length - 1;
    return (
      <div className="flex items-start gap-2 min-w-0">
        <Pill className="w-4 h-4 text-[#D95A40] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="truncate">{first}</div>
          {rest > 0 && (
            <div className="text-[11px] text-slate-500 mt-0.5">
              y {rest} producto{rest > 1 ? "s" : ""} más
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow={isAdmin ? "Todos los envíos" : "Historial"}
          title={isAdmin ? "Todos los Envíos" : "Mis Productos"}
          description={
            isAdmin
              ? "Envíos cargados por los clientes agrupados por documento XML."
              : "Historial completo de tus productos cargados desde XMLs."
          }
          actions={
            <Link
              to="/upload"
              data-testid="batches-upload-cta"
              className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
            >
              <Package className="w-4 h-4" />
              {isAdmin ? "Nuevo envío" : "Cargar productos"}
            </Link>
          }
        />

        <div className="bg-white border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0B1B3D] text-white">
                <tr>
                  <Th>Fecha</Th>
                  <Th>Producto</Th>
                  {isAdmin && <Th>Cliente</Th>}
                  <Th>Responsable</Th>
                  <Th className="text-right">Documentos</Th>
                  <Th className="text-right">Unidades</Th>
                  <Th className="text-right">Errores</Th>
                  <Th className="text-right">Acciones</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={isAdmin ? 8 : 7} className="p-8 text-center text-slate-500">
                      Cargando...
                    </td>
                  </tr>
                )}
                {!loading && batches.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 8 : 7} className="p-10 text-center">
                      <div className="text-slate-500 mb-3">
                        {isAdmin
                          ? "Aún no hay envíos cargados."
                          : "Aún no has cargado productos."}
                      </div>
                      <Link
                        to="/upload"
                        className="inline-flex items-center gap-2 text-[#D95A40] font-bold uppercase tracking-[0.15em] text-xs hover:underline"
                      >
                        Cargar primer XML →
                      </Link>
                    </td>
                  </tr>
                )}
                {batches.map((b) => (
                  <tr
                    key={b.id}
                    className="hover:bg-slate-50 transition-colors"
                    data-testid={`batch-row-${b.id}`}
                  >
                    <td className="px-4 py-3 tabular-nums text-slate-700 whitespace-nowrap align-top">
                      {new Date(b.created_at).toLocaleString("es-CL")}
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#0B1B3D] max-w-[280px]">
                      <Link
                        to={`/batches/${b.id}`}
                        className="hover:underline"
                        data-testid={`batch-open-${b.id}`}
                      >
                        {renderProduct(b)}
                      </Link>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-slate-600 text-xs align-top">
                        {b.user_email || "—"}
                      </td>
                    )}
                    <td className="px-4 py-3 text-slate-700 align-top">
                      {b.responsable || "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-display font-bold text-[#0B1B3D] tabular-nums align-top">
                      {b.total_docs}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700 align-top">
                      {(b.total_unidades ?? 0).toLocaleString("es-CL")}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums align-top">
                      {b.errors?.length ? (
                        <span className="inline-block bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-xs">
                          {b.errors.length}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/batches/${b.id}`}
                          data-testid={`batch-view-${b.id}`}
                          className="p-2 hover:bg-slate-100 text-slate-600 transition-colors"
                          title="Ver detalle"
                        >
                          <FileText className="w-4 h-4" />
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => downloadSap(b.id, b.descripcion)}
                            data-testid={`batch-download-sap-${b.id}`}
                            className="p-2 bg-[#0B1B3D] hover:bg-[#D95A40] text-white transition-colors"
                            title="Descargar SAP"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              data-testid={`batch-delete-${b.id}`}
                              className="p-2 hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {isAdmin
                                  ? "¿Eliminar este envío?"
                                  : "¿Eliminar este producto?"}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer. Se eliminarán{" "}
                                {b.total_docs} documento(s) procesado(s).
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => remove(b.id)}
                                data-testid={`batch-confirm-delete-${b.id}`}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em] ${className}`}
    >
      {children}
    </th>
  );
}
